import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HIGH_CONTRAST, USER_SELECTED_COUNTRY } from "../constants/storage";
import { Cookie } from "../services/storage";
import { ApplicationState } from "../store";
import {
  actionSetHideChangeLocationDrawer,
  actionSetHighContrast,
  actionSetSelectedCountry,
  actionSetTTSVoices,
} from "../store/actions/layout-actions";
import { LayoutState } from "../store/reducers/layout-reducer";
import { useCookie } from "./cookie-hooks";
import { useSpeechSynthesis } from "react-speech-kit";
import { HubCountry } from "../types/landing-page";
import { UserState } from "../store/reducers/user-reducer";
import { HubRegions } from "../data/landing-page";

export const useLayout = () => {
  const dispatch = useDispatch();
  const { isFCAllowed } = useCookie();

  const { voices } = useSpeechSynthesis();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { selectedCountry } = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  /**
   * Applies the dark theme to the document by setting the data-theme attribute.
   */
  const applyDarkMode = () => {
    document.documentElement.setAttribute("data-theme", "dark");
  };

  /**
   * Removes the theme from the document by removing the data-theme attribute.
   */
  const removeDarkMode = () => {
    document.documentElement.removeAttribute("data-theme");
  };

  useEffect(() => {
     // check system settings
     const isBrowserDefaultDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const hasContrastCookie = !!Cookie.get(HIGH_CONTRAST);
    // ========================================
    // DEPRECATED THEME: "hc"
    // ----------------------------------------
    // The "hc" (high contrast) theme is deprecated and
    // should not be used for future development.
    // Please use the "dark" theme instead.
    // This theme is retained temporarily for reference.
    // ========================================
    // apply corresponding theme based on system settings
    if (
      isBrowserDefaultDark &&
      !hasContrastCookie &&
      !layoutState.hasUserSelectedTheme
    ) {
      applyDarkMode();
      dispatch(actionSetHighContrast(true));
    }

    // override system settings if theme cookie exists 
    if (hasContrastCookie && layoutState.isDarkMode) {
      applyDarkMode();
      dispatch(actionSetHighContrast(true));
      
    } else if (hasContrastCookie && !layoutState.isDarkMode) {
      removeDarkMode();
      dispatch(actionSetHighContrast(false));
    }
  }, [layoutState.isDarkMode]);

  useEffect(() => {
    if (voices.length > 0) {
      dispatch(actionSetTTSVoices(voices));
    }
  }, [voices]);

  const setTheme = (isHc: boolean) => {
    dispatch(actionSetHighContrast(isHc));

    // If Functional Cookie is allowed, persist the user preference to cookie
    if (isFCAllowed) {
      Cookie.set(HIGH_CONTRAST, isHc.toString());
    }
    // Apply theme even if functional cookies are not allowed.
    if (isHc) {
      applyDarkMode();
    } else {
      removeDarkMode();
    }
  };

  const setSelectedCountry = (data: HubCountry) => {
    dispatch(actionSetSelectedCountry(data));
  };

  const getUserCountry = () => {
    const countryCode = selectedCountry.code
      ? selectedCountry.code
      : userCountryCode;

    const region = HubRegions.filter((region) =>
      region.countries.some((country) => country.code === countryCode)
    )[0];
    const country = region.countries.filter(
      (country) => country.code === countryCode
    )[0];

    setSelectedCountry(country);

    return country;
  };
  const setHideChangeLocationDrawer = (data: boolean) => {
    dispatch(actionSetHideChangeLocationDrawer(data));
  };
  const removeSelectedCountry = () => {
    Cookie.remove(USER_SELECTED_COUNTRY);
    dispatch(
      actionSetSelectedCountry({
        code: "",
        name: "",
        displayName: "",
        link: "",
      })
    );
  };

  return {
    setTheme,
    setSelectedCountry,
    getUserCountry,
    setHideChangeLocationDrawer,
    removeSelectedCountry,
  };
};
